// The two onboarding document registers (SEP-243 sections 4 and 5).
//
// This module holds the wire shapes, the catalogue of firm literature, and the
// PURE folds that state where each register stands. Nothing here stores a
// derived value: the panel, the controller and the final approval gate of a
// later slice all read the same helper, so the three cannot disagree. That is
// the rule `suitability-assessment-compute.ts` sets.
//
// The two registers hold opposite directions of travel, and the ticket keeps
// them apart. Register one is what the firm GIVES the client. Register two is
// what the firm ASKS the client for.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Register 1 - the documents the firm provides to the client (section 4)
// ---------------------------------------------------------------------------

/**
 * THE SERVICE TYPE DECIDES THE PACK (SEP-256 request 4).
 *
 * Zack: "Depending on service type (Discretionary, Advisory or Execution Only)
 * decides which documents the IM would send out to the client."
 *
 * The set is CLOSED, and this is its one definition on the wire. The firm
 * library tags each document with the service types that carry it, and the send
 * records the pack that went out. Each value is a domain value and not a wire
 * key, so `execution_only` is snake_case.
 *
 * The name carries the `pack` prefix because `domain-enums.ts` already exports a
 * legacy display-cased `ServiceType`. That one is a label of the client book,
 * and this one is the stored value that decides the pack. The two are not the
 * same set, so they must not share a name.
 */
export const packServiceTypeSchema = z.enum(['discretionary', 'advisory', 'execution_only']);
export type PackServiceType = z.infer<typeof packServiceTypeSchema>;

/** The labels the screen prints for each service type. */
export const PACK_SERVICE_TYPE_LABELS: Readonly<Record<PackServiceType, string>> = {
    discretionary: 'Discretionary',
    advisory: 'Advisory',
    execution_only: 'Execution Only',
};

/** The service types in the order every picker and every pill row prints them. */
export const PACK_SERVICE_TYPES: ReadonlyArray<PackServiceType> = [
    'discretionary',
    'advisory',
    'execution_only',
];

/**
 * THE ACCOUNT TYPE NARROWS THE PACK (SEP-346).
 *
 * Zack: "admin panel for me to be able to upload the welcome pack documents in
 * which the client will see in their portal depending on which service level &
 * account type they pick". The firm tags each file of its library with the
 * account types whose pack carries it. An empty list means every account type,
 * so each file from before SEP-346 stays in the pack of every account type.
 *
 * The set is the account types of the client application, one code for each
 * option of the `account_types` question. Each value is a domain value, so it
 * is snake_case. The Dowgate manifest adds the same codes to the vocabulary
 * `wealth.onboarding.account-types`, and a server test holds the two equal.
 */
export const packAccountTypeSchema = z.enum([
    'general_investment',
    'isa',
    'joint',
    'sipp',
    'company',
    'trust_charity',
    'ssas',
]);
export type PackAccountType = z.infer<typeof packAccountTypeSchema>;

/** The account types in the order of the application form. */
export const PACK_ACCOUNT_TYPES: ReadonlyArray<PackAccountType> = packAccountTypeSchema.options;

/** The labels the screen prints for each account type. */
export const PACK_ACCOUNT_TYPE_LABELS: Readonly<Record<PackAccountType, string>> = {
    general_investment: 'General Investment',
    isa: 'ISA',
    joint: 'Joint',
    sipp: 'SIPP',
    company: 'Company',
    trust_charity: 'Trust / Charity',
    ssas: 'SSAS',
};

/**
 * The account type of each option of the `account_types` question. `SASS` is
 * the spelling of the option before SEP-293, which an old draft can hold.
 */
const ACCOUNT_TYPE_OF_OPTION: Readonly<Record<string, PackAccountType>> = {
    'Individual General Investment': 'general_investment',
    ISA: 'isa',
    Joint: 'joint',
    SIPP: 'sipp',
    Company: 'company',
    'Trust / Charity': 'trust_charity',
    SSAS: 'ssas',
    SASS: 'ssas',
};

/**
 * The account types of one `account_types` answer, in form order. The answer
 * is a list of option labels, or one label in a draft from before SEP-280. A
 * label that names no account type is left out.
 */
export function packAccountTypesOfAnswer(value: unknown): PackAccountType[] {
    const labels = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
    const found = new Set<PackAccountType>();
    for (const label of labels) {
        if (typeof label !== 'string') continue;
        const type = ACCOUNT_TYPE_OF_OPTION[label.trim()];
        if (type) found.add(type);
    }
    return PACK_ACCOUNT_TYPES.filter((type) => found.has(type));
}

/**
 * True when a file with these account types goes to a client with these
 * account types.
 *
 * A file that names no account type goes to every client. A file that names
 * account types goes to a client who chose one of them. When the account types
 * of the client are not known (null or empty), only a file that names no
 * account type goes, because a guess could give the client the literature of
 * an account they did not open.
 */
export function packCarriesAccountTypes(
    fileAccountTypes: ReadonlyArray<string> | null | undefined,
    clientAccountTypes: ReadonlyArray<string> | null | undefined,
): boolean {
    if (!fileAccountTypes || fileAccountTypes.length === 0) return true;
    if (!clientAccountTypes || clientAccountTypes.length === 0) return false;
    return fileAccountTypes.some((type) => clientAccountTypes.includes(type));
}

export const providedDocumentAnswerSchema = z.enum(['yes', 'no', 'na']);
export type ProvidedDocumentAnswer = z.infer<typeof providedDocumentAnswerSchema>;

/**
 * WHEN THE FIRM SENDS ONE ITEM OF ITS LITERATURE (SEP-263 decision D6).
 *
 * Most items go out at the application meeting. Together they are what the
 * firm calls the Welcome Pack: the literature that the client acknowledges in
 * the portal. The Schedule of Charges is different: it goes out with the
 * Investment Mandate at Signing, once the case is approved (SEP-318, decision
 * 6), so `'final_pack'` marks it apart. The final approval gate reads
 * this value to build the set it demands BEFORE approval — a `final_pack` item
 * is not part of that set, because the firm has not reached the final pack
 * yet.
 */
export const providedDocumentDeliveryPointSchema = z.enum(['application', 'final_pack']);
export type ProvidedDocumentDeliveryPoint = z.infer<typeof providedDocumentDeliveryPointSchema>;

export const providedDocumentDeliveryMethodSchema = z.enum([
    'in_person',
    'email',
    'post',
    'portal',
    'docusign',
]);
export type ProvidedDocumentDeliveryMethod = z.infer<typeof providedDocumentDeliveryMethodSchema>;

/**
 * The labels the screen prints for each delivery method.
 *
 * Each key here is a domain VALUE and not a wire field name, so the snake_case
 * `in_person` is correct (CLAUDE.md, "Keys are camelCase. Values can be
 * snake_case"). The snake-case guard reads object-key syntax only, so it cannot
 * tell the two apart, and `in_person` therefore holds an entry in the
 * `ALLOWED_SNAKE_IDENTIFIERS` set of `apps/server/src/lib/lint/snake-case-guard.ts`.
 * A quoted key would also pass the guard, but prettier removes a quote that
 * nothing needs, so the quote would not survive the next commit.
 */
export const PROVIDED_DOCUMENT_DELIVERY_LABELS: Readonly<
    Record<ProvidedDocumentDeliveryMethod, string>
> = {
    in_person: 'In person',
    email: 'Email',
    post: 'Post',
    portal: 'Client portal',
    docusign: 'DocuSign',
};

/** One item of firm literature that the register can carry. */
export interface ProvidedDocumentCatalogueItem {
    /** The stored code. It is a value, so it is snake_case. */
    readonly code: string;
    readonly label: string;
    /** The firm treats the item as mandatory today. */
    readonly mandatory: boolean;
    /** The item needs a signed or recorded acknowledgement from the client. */
    readonly acknowledgementRequired: boolean;
    /**
     * The firm has settled whether it must give the item. SEP-243 section 4
     * names the Pershing and the ISA documentation as items to add "once the
     * firm confirms it", so those two carry `false` and they are not mandatory.
     */
    readonly confirmedByFirm: boolean;
    /**
     * WHEN the firm sends this item (SEP-263 decision D6). Every confirmed item
     * is `'application'` except the Schedule of Charges, which goes out with
     * the final pack. The final approval gate reads this field, through its own
     * mirror, to build the set it demands before approval.
     */
    readonly deliveryPoint: ProvidedDocumentDeliveryPoint;
}

/**
 * The catalogue of firm literature.
 *
 * SEP-243 section 4 names the first six items. It names the Pershing and the
 * ISA documentation as items to add once the firm confirms them, so those two
 * sit here with `confirmedByFirm: false` and `mandatory: false`. The summary
 * fold reports them apart from the outstanding mandatory set, so nobody reads
 * an unconfirmed item as a blocker.
 *
 * `OpenProvidedDocumentEntry` COPIES `mandatory` and `acknowledgementRequired`
 * onto the row. This catalogue can change, and a later approval gate must read
 * what was mandatory on the day of the case.
 *
 * `schedule_of_charges` carries `deliveryPoint: 'final_pack'` (SEP-263 decision
 * D6). The Mandate is generated FROM the final approved snapshot, and the
 * Schedule of Charges goes out WITH the Mandate and the Welcome Pack, so it
 * must not sit in the set the approval gate demands before approval. Every
 * other confirmed item keeps `deliveryPoint: 'application'`.
 */
export const PROVIDED_DOCUMENT_CATALOGUE: ReadonlyArray<ProvidedDocumentCatalogueItem> = [
    {
        code: 'risk_and_investment_guide',
        label: 'Risk and Investment Guide',
        mandatory: true,
        acknowledgementRequired: false,
        confirmedByFirm: true,
        deliveryPoint: 'application',
    },
    {
        code: 'capacity_for_loss_guide',
        label: 'Capacity for Loss Guide',
        mandatory: true,
        acknowledgementRequired: false,
        confirmedByFirm: true,
        deliveryPoint: 'application',
    },
    {
        code: 'terms_of_business',
        label: 'Terms of Business',
        mandatory: true,
        // The client signs the Terms of Business, so the register must hold the
        // acknowledgement and not only the delivery.
        acknowledgementRequired: true,
        confirmedByFirm: true,
        deliveryPoint: 'application',
    },
    {
        code: 'schedule_of_charges',
        label: 'Schedule of Charges',
        mandatory: true,
        acknowledgementRequired: false,
        confirmedByFirm: true,
        // SEP-263 decision D6. The firm sends this item with the final pack,
        // once the case is approved, so it must not block the approval itself.
        // The final pack goes out with the Investment Mandate at Signing
        // (SEP-318, decisions 6 and 9).
        deliveryPoint: 'final_pack',
    },
    {
        code: 'conflicts_of_interest_policy',
        label: 'Conflicts of Interest Policy',
        mandatory: true,
        acknowledgementRequired: false,
        confirmedByFirm: true,
        deliveryPoint: 'application',
    },
    {
        code: 'order_execution_policy',
        label: 'Order Execution Policy',
        mandatory: true,
        acknowledgementRequired: false,
        confirmedByFirm: true,
        deliveryPoint: 'application',
    },
    {
        code: 'pershing_documentation',
        label: 'Pershing documentation',
        mandatory: false,
        acknowledgementRequired: false,
        confirmedByFirm: false,
        deliveryPoint: 'application',
    },
    {
        code: 'isa_documentation',
        label: 'ISA documentation',
        mandatory: false,
        acknowledgementRequired: false,
        confirmedByFirm: false,
        deliveryPoint: 'application',
    },
];

/** Find one catalogue item, or undefined for a code the catalogue lost. */
export function providedDocumentCatalogueItem(
    code: string,
): ProvidedDocumentCatalogueItem | undefined {
    return PROVIDED_DOCUMENT_CATALOGUE.find((item) => item.code === code);
}

export const providedDocumentEntrySchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string().nullable(),
    documentCode: z.string(),
    documentLabel: z.string(),
    versionLabel: z.string().nullable(),
    provided: providedDocumentAnswerSchema,
    dateProvided: z.string().nullable(),
    providedByUserId: z.string().nullable(),
    deliveryMethod: providedDocumentDeliveryMethodSchema.nullable(),
    acknowledgementRequired: z.boolean(),
    acknowledgementReceivedAt: z.string().nullable(),
    acknowledgementReference: z.string().nullable(),
    /**
     * The instant the CLIENT acknowledged the live portal copy (SEP-343 item
     * 1). It is read from the portal acknowledgement and never stored on the
     * row, so the register and the document panel read one fact. Null while
     * the client has not acknowledged the document in the portal.
     */
    portalAcknowledgedAt: z.string().nullable().optional(),
    mandatory: z.boolean(),
    notes: z.string().nullable(),
    openedAt: z.string(),
    openedByUserId: z.string().nullable(),
    recordedAt: z.string().nullable(),
    /**
     * The act of delivery that settled this row (SEP-253). Null when nothing
     * settled it, and null when a person settled it row by row.
     */
    sendId: z.string().nullable(),
    /**
     * THE ONE LINK BETWEEN THE TWO RECORD SYSTEMS (SEP-263 decision D12).
     *
     * The `ClientPortalDocument` the platform published for this row, or null
     * when the firm gave the document by another route. The id is the shared
     * key of the two writes, so one publication holds one register row and one
     * client copy together.
     */
    portalDocumentId: z.string().nullable(),
    /**
     * The instant the CLIENT first opened the portal copy (SEP-263.35), read
     * from `ClientDocumentAccess`. It is null while the client has opened
     * nothing.
     *
     * IT IS NOT `openedAt`. `openedAt` is the instant a member of STAFF opened
     * the register row, and decision D12 forbids one field that means both.
     */
    clientOpenedAt: z.string().nullable(),
});
export type ProvidedDocumentEntryDto = z.infer<typeof providedDocumentEntrySchema>;

/**
 * ONE ACT OF DELIVERY (SEP-253).
 *
 * The investment manager emailed a pack of six documents on one day. That is
 * one act, and this is its wire shape. The rows it settled point back at it
 * through `ProvidedDocumentEntryDto.sendId`.
 *
 * `documents` carries the LABEL beside the code, resolved at the controller
 * from `PROVIDED_DOCUMENT_CATALOGUE`, so the screen needs no second lookup. A
 * code the catalogue no longer holds falls back to the code itself, which is
 * still readable and never blank.
 *
 * `versionLabel` comes from the STORED SEND ROW, and never from the catalogue
 * and never from the register row. The register row holds one version, so a
 * later send of the same document writes over it; the act keeps the version it
 * carried, and that is how a reader of two sends sees two versions.
 */
export const providedDocumentSendSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string().nullable(),
    /** The plain calendar day the firm sent the pack, `YYYY-MM-DD`. */
    sentOn: z.string(),
    deliveryMethod: providedDocumentDeliveryMethodSchema,
    /**
     * The pack that went out (SEP-256 request 4). Null when the act named no
     * pack: the firm sent packs before it recorded a service type, and an
     * investment manager may send one document that belongs to no pack.
     */
    serviceType: packServiceTypeSchema.nullable(),
    /**
     * What the act carried, as the act itself recorded it: the code, the label
     * of today, and the version that WENT OUT. `versionLabel` is null when the
     * firm held no version for the code on the day of the send.
     */
    documents: z.array(
        z.object({
            documentCode: z.string(),
            documentLabel: z.string(),
            versionLabel: z.string().nullable(),
        }),
    ),
    sentByUserId: z.string().nullable(),
    /** The directory name of the sender, or null when the id resolves to none. */
    sentByName: z.string().nullable(),
    reference: z.string().nullable(),
    notes: z.string().nullable(),
    recordedAt: z.string(),
    /** The instant somebody took the act back. Null while it stands. */
    withdrawnAt: z.string().nullable(),
    withdrawnByUserId: z.string().nullable(),
    withdrawnByName: z.string().nullable(),
});
export type ProvidedDocumentSendDto = z.infer<typeof providedDocumentSendSchema>;

/**
 * The version the FIRM holds for one item of its literature (SEP-253 request 5).
 *
 * The wire carries the live rows alone, because a screen asks "what is the
 * version of the Terms of Business today". The superseded rows stay in the
 * register, and nothing on the wire needs them yet.
 */
export const firmDocumentVersionSchema = z.object({
    documentCode: z.string(),
    versionLabel: z.string(),
    /** The plain calendar day the version takes effect, `YYYY-MM-DD`. */
    effectiveFrom: z.string(),
});
export type FirmDocumentVersionDto = z.infer<typeof firmDocumentVersionSchema>;

// ---------------------------------------------------------------------------
// Register 2 - the documents the firm receives from the client (section 5)
// ---------------------------------------------------------------------------

export const receivedDocumentStatusSchema = z.enum(['outstanding', 'received', 'checked', 'na']);
export type ReceivedDocumentStatus = z.infer<typeof receivedDocumentStatusSchema>;

/** The labels the screen prints for each status. */
export const RECEIVED_DOCUMENT_STATUS_LABELS: Readonly<Record<ReceivedDocumentStatus, string>> = {
    outstanding: 'Outstanding',
    received: 'Received',
    checked: 'Checked',
    na: 'Not applicable',
};

/**
 * The common document types, for the picker.
 *
 * The set is OPEN. SEP-243 section 5 lists the FIELDS of the row and it does
 * not close the types, so a member of staff can type another value. These eight
 * are the ones the onboarding pack asks for most often.
 */
export const RECEIVED_DOCUMENT_SUGGESTIONS: ReadonlyArray<string> = [
    'Proof of identity',
    'Proof of address',
    'Source of wealth evidence',
    'Source of funds evidence',
    'Bank account verification',
    'Tax residency declaration',
    'Signed Terms of Business',
    'Signed application form',
];

export const receivedDocumentRequirementSchema = z.object({
    id: z.string(),
    caseId: z.string(),
    clientId: z.string().nullable(),
    documentType: z.string(),
    status: receivedDocumentStatusSchema,
    dateReceived: z.string().nullable(),
    uploadedByUserId: z.string().nullable(),
    checkedByUserId: z.string().nullable(),
    /** The name of the person who checked the document (SEP-338). */
    checkedByName: z.string().nullable().optional(),
    checkedDate: z.string().nullable(),
    notes: z.string().nullable(),
    naReason: z.string().nullable(),
    requestedAt: z.string(),
    requestedByUserId: z.string().nullable(),
    /** The client Documents door minted these ids. */
    sourceUploadIds: z.array(z.string()),
});
export type ReceivedDocumentRequirementDto = z.infer<typeof receivedDocumentRequirementSchema>;

// ---------------------------------------------------------------------------
// The pure folds
// ---------------------------------------------------------------------------

/**
 * What the provided register says, in the terms the later approval gate asks
 * in.
 */
export interface ProvidedRegisterSummary {
    /** How many rows the register holds. */
    readonly total: number;
    /**
     * How many documents the case MUST settle. The count comes from the
     * catalogue and from the rows together, and not from the rows alone: a
     * mandatory item that nobody opened is still an item the firm must give.
     */
    readonly mandatoryTotal: number;
    /**
     * How many of those documents are SETTLED. A `yes` answer and an `na`
     * answer are both settled: an item that does not apply is not an item the
     * firm failed to give.
     */
    readonly mandatorySettled: number;
    /** The labels of the mandatory rows that carry a `no` answer. */
    readonly outstandingMandatory: ReadonlyArray<string>;
    /**
     * The labels of the mandatory catalogue items that the register does NOT
     * carry. An empty register holds every one of them, so an empty register
     * never settles the gate.
     */
    readonly missingMandatory: ReadonlyArray<string>;
    /**
     * The catalogue codes that the firm has not confirmed yet, and that the
     * register does not carry. They are NOT blockers.
     */
    readonly awaitingFirmConfirmation: ReadonlyArray<string>;
    /**
     * The labels of the rows that need an acknowledgement and hold none. A
     * `yes` answer with no acknowledgement is the only case that counts: a `no`
     * or an `na` answer means the client never got the document.
     *
     * IT IS INFORMATIONAL, AND IT IS NOT A BLOCKER (SEP-253 request 4). The
     * panel and the Internal Review print the list so the firm can chase the
     * signature, and `allMandatoryProvided` below never reads it.
     */
    readonly missingAcknowledgements: ReadonlyArray<string>;
    /**
     * TRUE when the case settled every mandatory document. The final approval
     * gate reads this one boolean (acceptance point 4).
     *
     * A MISSING ACKNOWLEDGEMENT DOES NOT MOVE IT (SEP-253 request 4). The firm
     * settled the rule: the IM may continue as soon as they have sent the pack
     * and ticked to say so. The signed copy comes BACK from the client, so it
     * is tracked in the received register and not here.
     */
    readonly allMandatoryProvided: boolean;
}

/**
 * The delivery point a code belongs to, for the fold below.
 *
 * A code the catalogue still recognises answers with the catalogue's own
 * `deliveryPoint`, whatever the row itself carries. A code the catalogue has
 * LOST answers `'application'`, which is the fold's old, single-set rule: a
 * retired code keeps binding the case that opened it, exactly as before SEP-263
 * decision D6 split the set in two.
 */
export function deliveryPointOf(code: string): ProvidedDocumentDeliveryPoint {
    return providedDocumentCatalogueItem(code)?.deliveryPoint ?? 'application';
}

/**
 * Fold the provided register, for ONE delivery point (SEP-263 decision D6).
 *
 * THE MANDATORY SET COMES FROM THE CATALOGUE, and not from the rows. A register
 * that nobody opened holds no row, and the rows alone would therefore report
 * that the firm gave the client everything it must give. The catalogue names
 * the items that the firm has confirmed for this delivery point, and each one
 * blocks the gate until a row settles it.
 *
 * A row that carries `mandatory: true` joins the set as well, because the row
 * keeps the copy of the flag from the day of the case: an item that leaves the
 * catalogue still binds a case that opened it. `deliveryPointOf` decides WHICH
 * fold a mandatory row joins, so a row that opened before D6 landed — when
 * every confirmed item still copied `mandatory: true` onto the row — is read
 * against the catalogue of TODAY and not against the stale copy on the row.
 */
function foldProvidedRegister(
    entries: ReadonlyArray<ProvidedDocumentEntryDto>,
    deliveryPoint: ProvidedDocumentDeliveryPoint,
): ProvidedRegisterSummary {
    const byCode = new Map(entries.map((e) => [e.documentCode, e]));
    const requiredCodes: string[] = [];
    for (const item of PROVIDED_DOCUMENT_CATALOGUE) {
        if (item.mandatory && item.confirmedByFirm && item.deliveryPoint === deliveryPoint) {
            requiredCodes.push(item.code);
        }
    }
    for (const entry of entries) {
        if (
            entry.mandatory &&
            !requiredCodes.includes(entry.documentCode) &&
            deliveryPointOf(entry.documentCode) === deliveryPoint
        ) {
            requiredCodes.push(entry.documentCode);
        }
    }
    const labelOf = (code: string): string =>
        byCode.get(code)?.documentLabel ?? providedDocumentCatalogueItem(code)?.label ?? code;
    const isSettled = (code: string): boolean => {
        const entry = byCode.get(code);
        return entry !== undefined && (entry.provided === 'yes' || entry.provided === 'na');
    };
    const outstandingMandatory = requiredCodes
        .filter((code) => byCode.has(code) && !isSettled(code))
        .map(labelOf);
    const missingMandatory = requiredCodes.filter((code) => !byCode.has(code)).map(labelOf);
    const missingAcknowledgements = entries
        .filter(
            (e) =>
                e.acknowledgementRequired &&
                e.provided === 'yes' &&
                e.acknowledgementReceivedAt === null &&
                // A portal acknowledgement settles the question (SEP-343 item 1).
                !e.portalAcknowledgedAt &&
                deliveryPointOf(e.documentCode) === deliveryPoint,
        )
        .map((e) => e.documentLabel);
    const awaitingFirmConfirmation = PROVIDED_DOCUMENT_CATALOGUE.filter(
        (item) =>
            !item.confirmedByFirm && item.deliveryPoint === deliveryPoint && !byCode.has(item.code),
    ).map((item) => item.code);
    return {
        total: entries.length,
        mandatoryTotal: requiredCodes.length,
        mandatorySettled: requiredCodes.filter(isSettled).length,
        outstandingMandatory,
        missingMandatory,
        awaitingFirmConfirmation,
        missingAcknowledgements,
        // The two facts are INDEPENDENT (SEP-253 request 4). Delivery settles a
        // mandatory document; the acknowledgement is chased apart from it.
        allMandatoryProvided: outstandingMandatory.length === 0 && missingMandatory.length === 0,
    };
}

/**
 * Fold the provided register for the items the firm gives AT THE APPLICATION
 * meeting (SEP-263 decision D6). This is the ONE fold the final approval gate
 * reads: a `final_pack` item, such as the Schedule of Charges, never joins the
 * set this function demands, so the gate never waits on a document the firm
 * only sends after approval.
 */
export function summariseProvidedDocuments(
    entries: ReadonlyArray<ProvidedDocumentEntryDto>,
): ProvidedRegisterSummary {
    return foldProvidedRegister(entries, 'application');
}

/**
 * Fold the provided register for the items the firm sends WITH THE FINAL PACK,
 * beside the Investment Mandate at Signing (SEP-263 decision D6, SEP-318
 * decision 6). The Schedule of Charges is the one item today.
 *
 * The final approval gate never reads this fold, and slice 10 of SEP-263 is
 * where the final pack itself is built and sent.
 */
export function summariseFinalPackDocuments(
    entries: ReadonlyArray<ProvidedDocumentEntryDto>,
): ProvidedRegisterSummary {
    return foldProvidedRegister(entries, 'final_pack');
}

/**
 * The register summary as a MUTABLE wire object (SEP-243 section 27).
 *
 * `ProvidedRegisterSummary` holds readonly arrays, and a Zod `z.array` infers a
 * mutable one. The Internal Review carries the summary on its pull-through
 * block, so both surfaces need this one copy step and neither one writes it
 * again by hand.
 */
export function providedRegisterSummaryDto(summary: ProvidedRegisterSummary): {
    total: number;
    mandatoryTotal: number;
    mandatorySettled: number;
    outstandingMandatory: string[];
    missingMandatory: string[];
    awaitingFirmConfirmation: string[];
    missingAcknowledgements: string[];
    allMandatoryProvided: boolean;
} {
    return {
        total: summary.total,
        mandatoryTotal: summary.mandatoryTotal,
        mandatorySettled: summary.mandatorySettled,
        outstandingMandatory: [...summary.outstandingMandatory],
        missingMandatory: [...summary.missingMandatory],
        awaitingFirmConfirmation: [...summary.awaitingFirmConfirmation],
        missingAcknowledgements: [...summary.missingAcknowledgements],
        allMandatoryProvided: summary.allMandatoryProvided,
    };
}

/** What the received register says (acceptance point 53). */
export interface ReceivedRegisterSummary {
    readonly total: number;
    readonly outstanding: number;
    readonly received: number;
    readonly checked: number;
    readonly notApplicable: number;
    /** The types that nobody has settled: still `outstanding` or `received`. */
    readonly unconfirmed: ReadonlyArray<string>;
    /**
     * TRUE when every requirement is `checked` or `na`. A `received` document
     * is NOT confirmed: somebody must look at it. An EMPTY register is not
     * confirmed either, because the firm has not yet asked for anything.
     */
    readonly allRequiredConfirmed: boolean;
}

export function summariseReceivedDocuments(
    requirements: ReadonlyArray<ReceivedDocumentRequirementDto>,
): ReceivedRegisterSummary {
    const count = (status: ReceivedDocumentStatus) =>
        requirements.filter((r) => r.status === status).length;
    const unconfirmed = requirements
        .filter((r) => r.status === 'outstanding' || r.status === 'received')
        .map((r) => r.documentType);
    return {
        total: requirements.length,
        outstanding: count('outstanding'),
        received: count('received'),
        checked: count('checked'),
        notApplicable: count('na'),
        unconfirmed,
        allRequiredConfirmed: requirements.length > 0 && unconfirmed.length === 0,
    };
}
